<p-toast></p-toast>

<app-page-shell>

  <app-page-hero eyebrow="Fuel Management" title="Fuel Management" subtitle="Marketplace connections, normalized fuel transactions, reconciliation, and consumption reporting.">
    <div nav>
      @for (nav of navItems; track nav.page) {
        <p-button [label]="nav.label" [icon]="nav.icon" [routerLink]="nav.route" [outlined]="activePage !== nav.page"></p-button>
      }
    </div>
    <div actions>
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" [loading]="loading" (onClick)="loadAll()"></p-button>
      <p-button label="Connect App" icon="fa-solid fa-plug" (onClick)="openConnection()"></p-button>
    </div>
  </app-page-hero>

  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

  @if (activePage === 'overview') {
    <div class="overview-grid">
      <section class="overview-panel">
        <div class="section-header">
          <div>
            <h2>Connected Apps</h2>
            <p>{{ connections.length }} configured</p>
          </div>
          <p-button label="Manage" size="small" [outlined]="true" routerLink="/fuel-management/connections"></p-button>
        </div>
        <p-table [value]="connections" [rows]="5" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '36rem' }">
          <ng-template pTemplate="header"><tr><th>Name</th><th>Provider</th><th>Status</th><th>Last Sync</th></tr></ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.displayName }}</td>
              <td>{{ providerName(row.providerCode) }}</td>
              <td><p-tag [value]="row.status || 'ACTIVE'" [severity]="statusSeverity(row.status)"></p-tag></td>
              <td>{{ row.lastSyncAt ? (row.lastSyncAt | date:'medium') : '-' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="4"><div class="empty-state"><i class="fa-solid fa-plug"></i><strong>No fuel apps connected</strong><span>Connect PetroApp, Aldrees WAIE, or a CSV source.</span></div></td></tr>
          </ng-template>
        </p-table>
      </section>

      <section class="overview-panel">
        <div class="section-header">
          <div>
            <h2>Recent Runs</h2>
            <p>{{ runs.length }} sync/import runs</p>
          </div>
          <p-button label="Marketplace" size="small" [outlined]="true" routerLink="/fuel-management/marketplace"></p-button>
        </div>
        <p-table [value]="runs" [rows]="5" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '36rem' }">
          <ng-template pTemplate="header"><tr><th>Run</th><th>Provider</th><th>Status</th><th class="text-right">Accepted</th></tr></ng-template>
          <ng-template pTemplate="body" let-run>
            <tr>
              <td>{{ run.runType }} #{{ run.runId }}</td>
              <td>{{ providerName(run.providerCode) }}</td>
              <td><p-tag [value]="run.status" [severity]="statusSeverity(run.status)"></p-tag></td>
              <td class="text-right">{{ run.acceptedCount || 0 }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="4"><div class="empty-state"><i class="pi pi-history"></i><strong>No runs yet</strong><span>Run a sync or import a CSV report.</span></div></td></tr>
          </ng-template>
        </p-table>
      </section>
    </div>
  }

  @if (activePage === 'marketplace') {
    <app-page-data-panel title="Fuel App Marketplace" subtitle="Provider adapters are isolated in the fuel service while REST API stores canonical fuel data." [resultText]="providers.length + ' apps'">
      <div class="provider-grid">
        @for (provider of providers; track provider.providerCode) {
          <article class="provider-card">
            <div class="provider-main">
              @if (provider.logoUrl) {
                <div class="provider-logo-wrap">
                  <img class="provider-logo" [src]="provider.logoUrl" [alt]="provider.displayName + ' logo'">
                </div>
              } @else {
                <span class="provider-icon"><i [class]="provider.logoIcon || 'fa-solid fa-gas-pump'"></i></span>
              }
              <div>
                <h3>{{ provider.displayName }}</h3>
                <p>{{ provider.description }}</p>
              </div>
            </div>
            <div class="provider-tags">
              @if (provider.supportsApiSync) { <p-tag value="API Sync" severity="success"></p-tag> }
              @if (provider.supportsCsvImport) { <p-tag [value]="importTagValue(provider)" severity="info"></p-tag> }
              @if (provider.supportsManualEntry) { <p-tag value="Manual" severity="secondary"></p-tag> }
            </div>
            <div class="provider-actions">
              @if (canConnectProvider(provider)) {
                <p-button label="Connect" icon="fa-solid fa-plug" size="small" (onClick)="openConnection(provider)"></p-button>
              }
              @if (canImportProvider(provider)) {
                <p-button [label]="importButtonLabel(provider)" icon="fa-solid fa-upload" size="small" severity="info" [outlined]="canConnectProvider(provider)" (onClick)="openCsvImport(provider)"></p-button>
              }
              @if (isManualProvider(provider)) {
                <span class="provider-action-note"><i class="fa-solid fa-check"></i> Built in</span>
              }
            </div>
          </article>
        }
      </div>
    </app-page-data-panel>
  }

  @if (activePage === 'connections') {
    <app-page-data-panel title="Fuel Connections" subtitle="Manage app credentials and trigger user-initiated sync/import runs." [resultText]="connections.length + ' connections'">
      <div actions>
        <p-button label="New Connection" icon="fa-solid fa-plus" size="small" (onClick)="openConnection()"></p-button>
      </div>

      <div class="import-strip">
        <p-inputGroup class="import-select">
          <p-inputGroupAddon><i class="fa-solid fa-plug"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select inputId="importConnection" [options]="connectionOptions" [(ngModel)]="selectedImportConnectionId" optionLabel="label" optionValue="value" appendTo="body" (onChange)="onImportTargetChanged()"></p-select>
            <label for="importConnection">Connection</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup class="import-select">
          <p-inputGroupAddon><i class="fa-solid fa-store"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select inputId="importProvider" [options]="importProviderOptions" [(ngModel)]="selectedImportProviderCode" optionLabel="label" optionValue="value" appendTo="body" (onChange)="onImportTargetChanged()"></p-select>
            <label for="importProvider">Provider</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-fileUpload #fuelCsvUploader mode="basic" [chooseLabel]="importChooseLabel" chooseIcon="pi pi-file-arrow-up" [accept]="importAccept" [customUpload]="true" (onSelect)="onFileSelected($event)" styleClass="fuel-upload-picker"></p-fileUpload>
        @if (selectedFile) {
          <span class="selected-file-name">{{ selectedFile.name }}</span>
        }
        <p-button [label]="importActionLabel" icon="fa-solid fa-upload" [loading]="importLoading" (onClick)="importCsv()"></p-button>
        <span class="import-help">{{ importHelpText }}</span>
      </div>

      <p-table [value]="connections" [loading]="loading" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '58rem' }">
        <ng-template pTemplate="header"><tr><th>Name</th><th>Provider</th><th>Status</th><th>Test</th><th>Sync</th><th class="table-actions">Actions</th></tr></ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><strong>{{ row.displayName }}</strong><span class="subtext">{{ row.baseUrl || 'Default service URL' }}</span></td>
            <td>{{ providerName(row.providerCode) }}</td>
            <td><p-tag [value]="row.status || 'ACTIVE'" [severity]="statusSeverity(row.status)"></p-tag></td>
            <td><span>{{ row.lastTestStatus || '-' }}</span><span class="subtext">{{ row.lastTestAt ? (row.lastTestAt | date:'short') : '' }}</span></td>
            <td><span>{{ row.lastSyncStatus || '-' }}</span><span class="subtext">{{ row.lastSyncAt ? (row.lastSyncAt | date:'short') : '' }}</span></td>
            <td class="table-actions">
              <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" (onClick)="openConnection(undefined, row)"></p-button>
              <p-button icon="pi pi-check-circle" [rounded]="true" [text]="true" severity="secondary" (onClick)="testConnection(row)"></p-button>
              <p-button icon="pi pi-sync" [rounded]="true" [text]="true" severity="success" (onClick)="syncConnection(row)"></p-button>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="6"><div class="empty-state"><i class="fa-solid fa-store"></i><strong>No connections</strong><span>Choose a marketplace app and add credentials.</span></div></td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  }

  @if (activePage === 'transactions') {
    <app-page-data-panel title="Fuel Transactions" subtitle="Canonical fuel transactions from API sync, CSV import, and manual entry." [resultText]="totalTransactions + ' records'">
      <div actions class="filters-row">
        <p-select [options]="providerOptions" [(ngModel)]="transactionFilters.providerCode" optionLabel="label" optionValue="value" placeholder="Provider" [showClear]="true" appendTo="body"></p-select>
        <p-select [options]="reconciliationOptions" [(ngModel)]="transactionFilters.reconciliationStatus" optionLabel="label" optionValue="value" placeholder="Status" appendTo="body"></p-select>
        <p-button label="Search" icon="pi pi-search" size="small" [loading]="transactionLoading" (onClick)="loadTransactions(0)"></p-button>
      </div>

      <p-table [value]="transactions" [lazy]="true" [paginator]="true" [rows]="transactionFilters.pageSize || 50" [totalRecords]="totalTransactions" [loading]="transactionLoading" (onLazyLoad)="onTransactionLazy($event)" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '72rem' }">
        <ng-template pTemplate="header"><tr><th>Date</th><th>Provider</th><th>Vehicle</th><th>Card</th><th class="text-right">Liters</th><th class="text-right">Amount</th><th>Station</th><th>Status</th><th class="table-actions">Actions</th></tr></ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.occurredAt | date:'medium' }}</td>
            <td>{{ providerName(row.providerCode) }}</td>
            <td>{{ row.vehiclePlateNumber || row.externalVehicleId || '-' }}</td>
            <td>{{ row.fuelCardNumber || '-' }}</td>
            <td class="text-right">{{ row.quantity | number:'1.2-2' }}</td>
            <td class="text-right">{{ row.totalAmount | number:'1.2-2' }} {{ row.currencyCode || 'SAR' }}</td>
            <td>{{ row.stationName || '-' }}</td>
            <td><p-tag [value]="row.reconciliationStatus" [severity]="statusSeverity(row.reconciliationStatus)"></p-tag></td>
            <td class="table-actions">
              <p-button icon="pi pi-link" [rounded]="true" [text]="true" severity="success" (onClick)="reconcile(row)"></p-button>
              <p-button icon="pi pi-ban" [rounded]="true" [text]="true" severity="secondary" (onClick)="ignore(row)"></p-button>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="9"><div class="empty-state"><i class="fa-solid fa-gas-pump"></i><strong>No fuel transactions</strong><span>Sync an app or import a fuel report.</span></div></td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  }

  @if (activePage === 'reports') {
    <app-page-data-panel title="Consumption Report" subtitle="Fuel spend and quantity grouped by matched vehicle or imported plate." [resultText]="reportRows.length + ' vehicles'">
      <p-table [value]="reportRows" [loading]="loading" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '54rem' }">
        <ng-template pTemplate="header"><tr><th>Vehicle</th><th class="text-right">Transactions</th><th class="text-right">Liters</th><th class="text-right">Avg Price</th><th class="text-right">Amount</th></tr></ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.vehiclePlateNumber || row.vehicleId || 'Unmatched' }}</td>
            <td class="text-right">{{ row.transactionCount || 0 }}</td>
            <td class="text-right">{{ row.totalQuantity | number:'1.2-2' }}</td>
            <td class="text-right">{{ row.averageUnitPrice | number:'1.4-4' }}</td>
            <td class="text-right">{{ row.totalAmount | number:'1.2-2' }} SAR</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="5"><div class="empty-state"><i class="fa-solid fa-chart-column"></i><strong>No report rows</strong><span>Fuel transactions will appear after sync or import.</span></div></td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  }
</app-page-shell>

<p-dialog header="Fuel Connection" [(visible)]="connectionDialogVisible" [modal]="true" [style]="{ width: 'min(58rem, 96vw)' }" styleClass="fuel-connection-dialog">
  <ng-template pTemplate="content">
    <form id="fuelConnectionForm" class="connection-form" [formGroup]="connectionForm" (ngSubmit)="saveConnection()">
      <p class="dialog-subtitle">Connect a provider for API sync, CSV import, or both.</p>

      <section class="form-section">
        <div class="section-title">
          <i class="fa-solid fa-plug"></i>
          <span>Provider</span>
        </div>
        <div class="form-grid">
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-plug"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="fuelProvider" [options]="connectionProviderOptions" formControlName="providerCode" optionLabel="label" optionValue="value" appendTo="body" (onChange)="onProviderChanged($event.value)"></p-select>
                <label for="fuelProvider">Provider</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelDisplayName" pInputText formControlName="displayName">
                <label for="fuelDisplayName">Display Name</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field field-span-2">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelBaseUrl" pInputText formControlName="baseUrl" readonly>
                <label for="fuelBaseUrl">Base URL</label>
              </p-iftaLabel>
            </p-inputGroup>
            <small>Provider host used before endpoint paths.</small>
          </div>
        </div>
      </section>

      <section class="form-section">
        <div class="section-title">
          <i class="fa-solid fa-key"></i>
          <span>Authentication</span>
        </div>
        <div class="form-grid">
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-code"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelWsVersion" pInputText formControlName="wsVersion">
                <label for="fuelWsVersion">WS Version</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-key"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelWsSk" pInputText type="password" formControlName="wsSk" autocomplete="new-password">
                <label for="fuelWsSk">WS-SK</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-lock"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelApiKey" pInputText type="password" formControlName="apiKey" autocomplete="new-password">
                <label for="fuelApiKey">API Key</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-ticket"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelApiKeyHeader" pInputText formControlName="apiKeyHeader">
                <label for="fuelApiKeyHeader">API Key Header</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-shield-halved"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelBearerToken" pInputText type="password" formControlName="bearerToken" autocomplete="new-password">
                <label for="fuelBearerToken">Bearer Token</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelSubscriptionKey" pInputText type="password" formControlName="subscriptionKey" autocomplete="new-password">
                <label for="fuelSubscriptionKey">Subscription Key</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
        </div>
      </section>

      <section class="form-section">
        <div class="section-title">
          <i class="fa-solid fa-code-branch"></i>
          <span>API Endpoints</span>
        </div>
        <div class="form-grid">
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-send"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="fuelHttpMethod" [options]="httpMethodOptions" formControlName="httpMethod" optionLabel="label" optionValue="value" appendTo="body"></p-select>
                <label for="fuelHttpMethod">HTTP Method</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-sync"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelSyncPath" pInputText formControlName="transactionsPath" placeholder="/api/transactions">
                <label for="fuelSyncPath">Sync Path</label>
              </p-iftaLabel>
            </p-inputGroup>
            <small>Endpoint path that returns fuel transactions.</small>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-check-circle"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelTestPath" pInputText formControlName="testPath" placeholder="/api/health">
                <label for="fuelTestPath">Test Path</label>
              </p-iftaLabel>
            </p-inputGroup>
            <small>Optional lightweight endpoint used to test credentials.</small>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-list"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="fuelHeadersJson" pInputText formControlName="headersJson" placeholder='{"X-Tenant":"123"}'>
                <label for="fuelHeadersJson">Headers JSON</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field field-span-2">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-file-edit"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="fuelRequestBody" pTextarea formControlName="requestBody" rows="3"></textarea>
                <label for="fuelRequestBody">Request Body</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
        </div>
      </section>

      <section class="form-section">
        <div class="section-title">
          <i class="fa-solid fa-gear"></i>
          <span>Connection Settings</span>
        </div>
        <div class="field field-span-2">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <textarea id="fuelNotes" pTextarea formControlName="notes" rows="3"></textarea>
              <label for="fuelNotes">Notes</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
        <div class="toggle-row">
          <label class="active-toggle" for="fuelActive">
            <p-checkbox formControlName="active" [binary]="true" inputId="fuelActive"></p-checkbox>
            <span>
              <strong>Active</strong>
              <small>Connection can be used for sync and import.</small>
            </span>
          </label>
          <label class="active-toggle" for="fuelDefault">
            <p-checkbox formControlName="defaultConnection" [binary]="true" inputId="fuelDefault"></p-checkbox>
            <span>
              <strong>Default</strong>
              <small>Use first when importing reports for this provider.</small>
            </span>
          </label>
        </div>
      </section>
    </form>
  </ng-template>
  <ng-template pTemplate="footer">
    <div class="dialog-actions">
      <p-button label="Cancel" severity="secondary" [outlined]="true" type="button" (onClick)="connectionDialogVisible = false"></p-button>
      <p-button label="Save" icon="pi pi-save" type="button" [loading]="saving" (onClick)="saveConnection()"></p-button>
    </div>
  </ng-template>
</p-dialog>
